import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function Loader({ onDone }) {
  const { t, i18n } = useTranslation();
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const t1 = setTimeout(() => setVisible(false), 2400);
    const t2 = setTimeout(() => onDone && onDone(), 3000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [onDone]);

  const isAr = i18n.language === 'ar';

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="loader-screen"
          className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden"
          style={{ background: '#10092E' }}
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, filter: 'blur(20px)' }}
          transition={{ duration: 0.8, ease: [0.65, 0, 0.35, 1] }}
        >
          {/* soft radial lights */}
          <div className="absolute inset-0 dwk-noise" aria-hidden="true">
            <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full"
                 style={{ background: 'radial-gradient(closest-side, rgba(137, 72, 195,0.5), transparent)' }} />
            <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full"
                 style={{ background: 'radial-gradient(closest-side, rgba(64, 211, 190,0.4), transparent)' }} />
          </div>

          <div className="relative">
            {/* Logo placeholder + drawn line */}
            <div className="relative w-[220px] h-[220px] flex items-center justify-center">
              <svg className="absolute inset-0" viewBox="0 0 220 220" width="220" height="220" aria-hidden="true">
                <defs>
                  <linearGradient id="loaderStroke" x1="0" x2="1" y1="0" y2="1">
                    <stop offset="0%" stopColor="#8948C3" />
                    <stop offset="100%" stopColor="#40D3BE" />
                  </linearGradient>
                </defs>
                <motion.rect
                  x="10" y="10" width="200" height="200" rx="24"
                  fill="none"
                  stroke="url(#loaderStroke)"
                  strokeWidth="1.5"
                  initial={{ pathLength: 0, opacity: 0.4 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{ duration: 1.8, ease: [0.65, 0, 0.35, 1] }}
                />
              </svg>
              <motion.div
                className="text-center"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.9 }}
              >
                <div className={`text-white text-4xl md:text-5xl font-bold tracking-tight ${isAr ? 'font-ar' : 'font-en'}`}>
                  <span className="dwk-gradient-text-shine">{t('loader.brand')}</span>
                </div>
                <div className="mt-3 text-white/60 text-[11px] tracking-[0.35em] uppercase">
                  {t('loader.tagline')}
                </div>
              </motion.div>
            </div>
            <motion.div
              className="mx-auto mt-8 h-[2px] w-40 overflow-hidden rounded-full bg-white/10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <motion.div
                className="h-full dwk-gradient"
                initial={{ x: '-100%' }}
                animate={{ x: '0%' }}
                transition={{ duration: 1.8, ease: [0.65, 0, 0.35, 1] }}
              />
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
